import { LazyHandle } from './lazyHandle.js';
import type {
  HostSlotLoaderPort,
  HostSlotOutcome,
} from './runtime/hostSlotPort.js';
import type { LoaderRuntimeOptions } from './runtime/runtime.js';
import {
  createNewRelicTelemetry,
  guardTelemetry,
  type Telemetry,
} from './runtime/telemetry.js';

export type { ConsentGate, ConsentState } from './runtime/consent.js';
export { LoaderError } from './runtime/containment.js';
export { hostServedTemplateRefusal } from './runtime/iframe/buildSrc.js';
export type { IframeEntry } from './runtime/iframe/iframeIntegration.js';
export { DEFAULT_SANDBOX, SANDBOX_GRANTS } from './runtime/iframe/sandbox.js';
export type { SandboxGrant } from './runtime/iframe/sandbox.js';
export type { IframeSizing } from './runtime/iframe/sizing.js';
export type {
  HostSlotContext,
  HostSlotFillRequest,
  HostSlotHandle,
  HostSlotLoaderPort,
  HostSlotOutcome,
  HostSlotOutputs,
  HostSlotReasonCode,
} from './runtime/hostSlotPort.js';
export { EXACT_SEMVER_PATTERN } from './runtime/module/moduleUrl.js';
export type {
  ModuleContainerKind,
  ModuleEntry,
} from './runtime/module/moduleUrl.js';
export type {
  Integration,
  IntegrationMountArgs,
  Integrations,
  LoadedArtifact,
  RegistryEntry,
  RegistrySnapshot,
} from './runtime/integrations.js';
export {
  BUDGET_MAX_KB,
  BUDGET_MIN_KB,
  DEFAULT_BUDGET_KB,
  TIMEOUT_MAX_MS,
  TIMEOUT_MIN_MS,
  readyDeadlineMs,
  resolveLimits,
} from './runtime/limits.js';
export type { Limits } from './runtime/limits.js';
export type { LoaderRuntimeOptions } from './runtime/runtime.js';
export {
  DEFAULT_SCRIPT_REFERRER_POLICY,
  SCRIPT_REFERRER_POLICIES,
} from './runtime/script/scriptIntegration.js';
export type {
  ScriptEntry,
  ScriptReferrerPolicy,
} from './runtime/script/scriptIntegration.js';
export {
  TELEMETRY_ACTION_NAME,
  createNewRelicTelemetry,
} from './runtime/telemetry.js';
export type { OutcomeAttributes, Telemetry } from './runtime/telemetry.js';

/**
 * Builds the loader a host hands to `HostSlotLoaderProvider`. The runtime is a
 * separate chunk, fetched when the first slot fills, so a page with no slot
 * never loads it.
 */
export function createHostSlotLoader(
  options: LoaderRuntimeOptions,
): HostSlotLoaderPort {
  const telemetry: Telemetry = guardTelemetry(
    options.telemetry ?? createNewRelicTelemetry(),
  );
  let runtime: Promise<HostSlotLoaderPort> | undefined;

  const loadRuntime = (): Promise<HostSlotLoaderPort> => {
    runtime ??= import('./runtime/runtime.js').then(
      (chunk) => chunk.createLoaderRuntime({ ...options, telemetry }),
      (error: unknown) => {
        // A failed chunk fetch is retried by the next placement.
        runtime = undefined;
        throw error;
      },
    );
    return runtime;
  };

  return {
    flowTenantId: options.flowTenantId,
    fill: (request) =>
      new LazyHandle(request, loadRuntime, telemetry, options.tenantSlug),
    reportOutcome(outcome: HostSlotOutcome) {
      loadRuntime().then(
        (loaded) => loaded.reportOutcome?.(outcome),
        () => undefined,
      );
    },
  };
}
